import { DatePipe } from "@angular/common";
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material";
import {
  IFilterResponse,
  SearchFilter,
} from "src/app/infrastructure/iFilter-component";
import { SearchMedicalReportHistorialResponse } from "src/app/model/study-report/search-medical-report-historial-response";
import { MenuService } from "src/app/services/menu.service";
import { NotificationToastService } from "src/app/services/notification-toast.service";
import { PdfService } from "src/app/services/pdf.service";
import { StudyReportService } from "src/app/services/study-report/study-report.service";
import {
  StudyReportViewerDialogComponent,
  StudyReportViewerDialogRequest,
} from "../../dialogs/study-report-viewer/study-report-viewer-dialog.component";
import { PagerControlComponent } from "../../controls/pager/pager-control.component";
import { SearchFilterComponent } from "../../search-filter/search-filter.component";

interface ColumnDefinition {
  field: string;
  title: string;
  visible: boolean;
  order: number;
  width: number;
}

@Component({
  selector: "app-study-report-historial-search",
  templateUrl: "./study-report-historial-search.component.html",
  styleUrls: ["./study-report-historial-search.component.css"],
  providers: [DatePipe],
})
export class StudyReportHistorialSearchComponent implements OnInit, OnDestroy {
  loading = false;
  loadingAction = false;
  searchFilters: SearchFilter[] = [];
  allItems: SearchMedicalReportHistorialResponse[] = [];
  dataSource: SearchMedicalReportHistorialResponse[] = [];
  columnFilters: { [field: string]: string } = {};

  @ViewChild("searchFilter", { static: false })
  searchFilter!: SearchFilterComponent;
  @ViewChild("pager", { static: true }) pager!: PagerControlComponent;

  private sessionColumnsKey = "study-report-historial-search.columns.v1";
  private sessionSortKey = "study-report-historial-search.sort.v1";
  private sessionColumnWidthsKey = "study-report-historial-search.column-widths.v1";

  private sortField: string | null = null;
  private sortDirection: "asc" | "desc" = "asc";
  private dragSourceField: string | null = null;
  private resizeListeners: { move: EventListener; up: EventListener } | null = null;

  columnsDefinition: ColumnDefinition[] = [
    { field: "date", title: "Fecha", visible: true, order: 0, width: 130 },
    { field: "specialtyName", title: "Especialidad", visible: true, order: 2, width: 150 },
    { field: "fileReason", title: "Motivo", visible: true, order: 3, width: 90 },
    { field: "observations", title: "Observaciones", visible: true, order: 4, width: 220 },
    { field: "patient", title: "Paciente", visible: true, order: 5, width: 180 },
    { field: "practice", title: "Práctica", visible: true, order: 6, width: 180 },
    { field: "protocol", title: "Protocolo", visible: true, order: 7, width: 110 },
    { field: "reportRegion", title: "Región", visible: true, order: 8, width: 150 },
    { field: "reportingPhysician", title: "Médico", visible: true, order: 9, width: 110 },
  ];

  get availableColumns(): ColumnDefinition[] {
    return this.columnsDefinition;
  }

  get orderedColumnsDefinition(): ColumnDefinition[] {
    return this.columnsDefinition
      .filter((column) => column.visible)
      .sort((a, b) => a.order - b.order);
  }

  constructor(
    public dialog: MatDialog,
    private menuService: MenuService,
    private datePipe: DatePipe,
    private studyReportService: StudyReportService,
    private pdfService: PdfService,
    private notificationToastService: NotificationToastService
  ) {
    this.menuService.setMenuTitle("Informe", "Versionado informes");
  }

  ngOnInit(): void {
    this.restoreColumns();
    this.restoreSort();
    this.restoreColumnWidths();
  }

  ngOnDestroy(): void {
    this.removeResizeListeners();
  }

  selectedPageIndexChange(pagedItems: SearchMedicalReportHistorialResponse[]) {
    this.dataSource = [...pagedItems];
    this.loading = false;
  }

  onStartSearch() {
    this.loading = true;
  }

  onEndSearch(response: IFilterResponse<SearchMedicalReportHistorialResponse>) {
    this.allItems = response.items || [];
    this.searchFilters = response.searchFilters || [];
    this.refreshGrid();
  }

  async viewHistorial(item: SearchMedicalReportHistorialResponse) {
    if (!item || !item.ReportFileId) {
      this.notificationToastService.error(
        "No existe archivo de historial para visualizar.",
        "Versionado informes"
      );
      return;
    }

    try {
      this.loadingAction = true;
      const report = await this.studyReportService.getStudyReportHistorial(
        item.ReportFileId
      );

      if (report && report.ReportBase64) {
        const studyReportViewerDialogRequest =
          new StudyReportViewerDialogRequest();
        studyReportViewerDialogRequest.CanConfirm = false;

        const pdfUrl = this.pdfService.base64ToPdfUrl(report.ReportBase64);
        studyReportViewerDialogRequest.SafeResourceUrl =
          this.pdfService.pdfUrlToPdfSafeResourceUrl(pdfUrl);

        this.dialog.open(StudyReportViewerDialogComponent, {
          width: "90%",
          height: "600px",
          data: studyReportViewerDialogRequest,
        });
      } else {
        this.notificationToastService.error(
          "No se encontró un historial para el archivo seleccionado.",
          "Versionado informes"
        );
      }
    } catch (err) {
      const error = err as any;
      this.notificationToastService.error(
        JSON.stringify(error && error.error ? error.error : error),
        "Versionado informes"
      );
    } finally {
      this.loadingAction = false;
    }
  }

  getCellValue(
    item: SearchMedicalReportHistorialResponse,
    field: string
  ): string {
    switch (field) {
      case "date":
        return item.Date
          ? this.datePipe.transform(item.Date, "dd/MM/yyyy HH:mm") || ""
          : "";
      case "reportFileId":
        return item.ReportFileId !== null && item.ReportFileId !== undefined
          ? item.ReportFileId.toString()
          : "";
      case "specialtyName":
        return item.SpecialtyName || "";
      case "fileReason":
        return item.FileReason || "";
      case "observations":
        return item.Observations || "";
      case "patient":
        return item.Patient || "";
      case "practice":
        return item.Practice || "";
      case "protocol":
        return item.Protocol || "";
      case "reportRegion":
        return item.ReportRegion || "";
      case "reportingPhysician":
        return item.ReportingPhysician || "";
      default:
        return "";
    }
  }

  isColumnVisible(field: string): boolean {
    const column = this.columnsDefinition.find((item) => item.field === field);
    return column ? column.visible : false;
  }

  setColumnVisibility(field: string, visible: boolean) {
    const column = this.columnsDefinition.find((item) => item.field === field);
    if (column) {
      column.visible = visible;
      this.saveColumns();
      this.refreshGrid();
    }
  }

  showAllColumns() {
    this.columnsDefinition.forEach((column) => (column.visible = true));
    this.saveColumns();
    this.refreshGrid();
  }

  sortByColumn(field: string) {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortField = field;
      this.sortDirection = "asc";
    }
    this.saveSort();
    this.refreshGrid();
  }

  getSortIcon(field: string): string {
    if (this.sortField !== field) {
      return "unfold_more";
    }
    return this.sortDirection === "asc" ? "arrow_upward" : "arrow_downward";
  }

  onColumnFilterInput(field: string, value: string) {
    this.columnFilters[field] = value;
    this.refreshGrid();
  }

  getColumnWidth(field: string): number {
    const column = this.columnsDefinition.find((item) => item.field === field);
    return column ? column.width : 150;
  }

  onHeaderDragStart(event: DragEvent, field: string) {
    this.dragSourceField = field;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
    }
  }

  onHeaderDragOver(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }
  }

  onHeaderDrop(event: DragEvent, targetField: string) {
    event.preventDefault();
    if (!this.dragSourceField || this.dragSourceField === targetField) {
      return;
    }

    const visibleColumns = this.orderedColumnsDefinition;
    const fromIndex = visibleColumns.findIndex(
      (item) => item.field === this.dragSourceField
    );
    const toIndex = visibleColumns.findIndex(
      (item) => item.field === targetField
    );

    if (fromIndex < 0 || toIndex < 0) {
      return;
    }

    const moved = visibleColumns.splice(fromIndex, 1)[0];
    visibleColumns.splice(toIndex, 0, moved);
    visibleColumns.forEach((column, index) => {
      const original = this.columnsDefinition.find(
        (item) => item.field === column.field
      );
      if (original) {
        original.order = index;
      }
    });
    this.saveColumns();
    this.refreshGrid();
  }

  onHeaderDragEnd() {
    this.dragSourceField = null;
  }

  startResize(event: MouseEvent, field: string) {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const column = this.columnsDefinition.find((item) => item.field === field);
    const startWidth = column ? column.width : 150;

    this.removeResizeListeners();

    const moveHandler: EventListener = (moveEvent: Event) => {
      const mouseEvent = moveEvent as MouseEvent;
      const newWidth = Math.max(50, startWidth + (mouseEvent.clientX - startX));
      if (column) {
        column.width = newWidth;
      }
    };

    const upHandler: EventListener = () => {
      this.removeResizeListeners();
      this.saveColumnWidths();
    };

    document.addEventListener("mousemove", moveHandler);
    document.addEventListener("mouseup", upHandler);
    this.resizeListeners = { move: moveHandler, up: upHandler };
  }

  private removeResizeListeners() {
    if (this.resizeListeners) {
      document.removeEventListener("mousemove", this.resizeListeners.move);
      document.removeEventListener("mouseup", this.resizeListeners.up);
      this.resizeListeners = null;
    }
  }

  private saveColumns() {
    const state = this.columnsDefinition.map((column) => ({
      field: column.field,
      visible: column.visible,
      order: column.order,
    }));
    sessionStorage.setItem(this.sessionColumnsKey, JSON.stringify(state));
  }

  private restoreColumns() {
    const raw = sessionStorage.getItem(this.sessionColumnsKey);
    if (!raw) {
      return;
    }
    try {
      const state: { field: string; visible: boolean; order: number }[] =
        JSON.parse(raw);
      state.forEach((savedColumn) => {
        const column = this.columnsDefinition.find(
          (item) => item.field === savedColumn.field
        );
        if (column) {
          column.visible = savedColumn.visible;
          column.order = savedColumn.order;
        }
      });
    } catch {}
  }

  private saveSort() {
    sessionStorage.setItem(
      this.sessionSortKey,
      JSON.stringify({ field: this.sortField, direction: this.sortDirection })
    );
  }

  private restoreSort() {
    const raw = sessionStorage.getItem(this.sessionSortKey);
    if (!raw) {
      return;
    }
    try {
      const saved = JSON.parse(raw);
      this.sortField = saved.field;
      this.sortDirection = saved.direction;
    } catch {}
  }

  private saveColumnWidths() {
    const widths: { [field: string]: number } = {};
    this.columnsDefinition.forEach((column) => (widths[column.field] = column.width));
    sessionStorage.setItem(this.sessionColumnWidthsKey, JSON.stringify(widths));
  }

  private restoreColumnWidths() {
    const raw = sessionStorage.getItem(this.sessionColumnWidthsKey);
    if (!raw) {
      return;
    }
    try {
      const widths: { [field: string]: number } = JSON.parse(raw);
      this.columnsDefinition.forEach((column) => {
        if (widths[column.field] !== undefined) {
          column.width = widths[column.field];
        }
      });
    } catch {}
  }

  private getNormalizedColumnValue(
    item: SearchMedicalReportHistorialResponse,
    field: string
  ): string {
    return this.normalizeFilterValue(this.getCellValue(item, field));
  }

  private normalizeFilterValue(value: string): string {
    if (!value) {
      return "";
    }
    return value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  private applyColumnFilters(
    items: SearchMedicalReportHistorialResponse[]
  ): SearchMedicalReportHistorialResponse[] {
    return items.filter((item) => {
      return Object.keys(this.columnFilters).every((field) => {
        const filterVal = this.normalizeFilterValue(this.columnFilters[field]);
        if (!filterVal) {
          return true;
        }
        const cellVal = this.getNormalizedColumnValue(item, field);
        return cellVal.includes(filterVal);
      });
    });
  }

  private sortAllData(
    items: SearchMedicalReportHistorialResponse[]
  ): SearchMedicalReportHistorialResponse[] {
    const sortField = this.sortField;
    if (!sortField) {
      return items;
    }

    const direction = this.sortDirection === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
      const valueA = this.getNormalizedColumnValue(a, sortField);
      const valueB = this.getNormalizedColumnValue(b, sortField);
      return valueA < valueB ? -direction : valueA > valueB ? direction : 0;
    });
  }

  private refreshGrid() {
    const filtered = this.applyColumnFilters(this.allItems);
    const sorted = this.sortAllData(filtered);
    this.pager.setItems(sorted);
  }
}
